import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError } from '@/api/client';
import type { OrderListItem, OrderPage, OrderStats } from '@/api/types';

export function saveOrderLedgerColumns(scope: 'mine' | 'all', keys: string[] | null) {
  return api.patch('/api/v2/orders/ledger-columns', { scope, keys });
}
export function assignOrderFollower(orderId: string, followerUserId: string, expectedFollowerUserId: string | null) {
  return api.post(`/api/v2/orders/${encodeURIComponent(orderId)}/assign`, { followerUserId, expectedFollowerUserId });
}

/** 开单和提交均走 V2 写门禁，并复用 V1 的共享订单与预占逻辑。 */
export function createOrderDraft(payload: Record<string, unknown>) {
  return api.post<{ id: string; orderNo: string }>('/api/v2/orders', payload);
}

/** 开单时新建客户写入 V1/V2 共用的客户表；生产写请求由 api/client 门禁阻止。 */
export function createOrderCustomer(payload: { name: string; contact?: string; phone?: string; address?: string }) {
  return api.post<{ id: string; name: string; contact?: string | null; phone?: string | null; address?: string | null }>('/api/customers', payload);
}

export function submitOrderDraft(id: string) {
  return api.post(`/api/v2/orders/${encodeURIComponent(id)}/submit`, {});
}

export function uploadOrderAttachment(file: File) {
  const body = new FormData();
  body.append('file', file);
  return api.postForm<{ url: string }>('/api/v2/uploads', body);
}

export function removeUnreferencedOrderAttachment(url: string) {
  return api.delete(`/api/v2/uploads?url=${encodeURIComponent(url)}`);
}

export function attachOrderFile(orderId: string, file: { fileName: string; fileUrl: string; fileType: string; fileSize: number; usageNote?: string }) {
  return api.post(`/api/v2/orders/${encodeURIComponent(orderId)}/attachments`, file);
}

/** V2 本地异常登记复用 V1 的业务写入与共享订单异常记录；生产默认禁写。 */
export function createOrderException(id: string, payload: { type: 'DAMAGED' | 'SHORT_SHIPPED' | 'RETURNED'; shipmentId?: string; orderLineId: string; quantity: number; note?: string; responsibility?: string }) {
  return api.post<{ id: string }>(`/api/v2/orders/${encodeURIComponent(id)}/exceptions`, payload);
}

export function uploadExceptionEvidence(orderId: string, photo: { id: string; file: File; label: string; capturedAt: string }, exceptionIds: string[]) {
  const form = new FormData();
  form.set('file', photo.file); form.set('requestKey', photo.id); form.set('label', photo.label);
  form.set('capturedAt', photo.capturedAt); form.set('exceptionIds', JSON.stringify(exceptionIds));
  return api.postForm<{ id: string; url: string; replay: boolean }>(`/api/v2/orders/${encodeURIComponent(orderId)}/exceptions/evidence`, form);
}

export function resolveOrderException(orderId: string, exceptionId: string, payload: { status: 'PROCESSING' | 'RESOLVED'; resolutionAction?: 'NOTED'; resolutionNote: string; responsibility?: string; refundAmount?: number; deductionAmount?: number }) {
  return api.patch<{ id: string }>(`/api/v2/orders/${encodeURIComponent(orderId)}/exceptions/${encodeURIComponent(exceptionId)}`, payload);
}

export function settleOrderExceptionFinancial(orderId: string, exceptionId: string, payload: { action: 'REFUND' | 'CONCESSION'; amount: number; note: string; responsibility: string; method?: string; referenceNo?: string }) {
  return api.post<{ id: string; status: string; cashRecordId: string | null }>(`/api/v2/orders/${encodeURIComponent(orderId)}/exceptions/${encodeURIComponent(exceptionId)}/financial`, payload);
}

export function updateOrderExceptionResponsibility(orderId: string, exceptionId: string, responsibility: string) {
  return api.patch<{ id: string }>(`/api/v2/orders/${encodeURIComponent(orderId)}/exceptions/${encodeURIComponent(exceptionId)}`, { responsibility });
}

export function saveOrderExceptionClaim(orderId: string, exceptionId: string, payload: { expectedUpdatedAt: string; amount: number; status: string | null; recoveredAmount: number }) {
  return api.post<{ id: string; updatedAt: string }>(`/api/v2/orders/${encodeURIComponent(orderId)}/exceptions/${encodeURIComponent(exceptionId)}/claim`, payload);
}

export function closeOrderException(orderId: string, exceptionId: string, payload: { status: 'CLOSED'; resolutionAction: 'NOTED' | 'RESHIPPED' | 'REFUNDED' | 'IGNORED' | 'TRANSFERRED'; resolutionNote: string }) {
  return api.patch<{ id: string }>(`/api/v2/orders/${encodeURIComponent(orderId)}/exceptions/${encodeURIComponent(exceptionId)}`, payload);
}

export function reshipOrderException(orderId: string, exceptionId: string, payload: { quantity: number; logisticsCompany: string; trackingNo: string; note?: string }) {
  return api.post<{ id: string }>(`/api/v2/orders/${encodeURIComponent(orderId)}/exceptions/${encodeURIComponent(exceptionId)}/reship`, payload);
}

export function returnInboundOrderException(orderId: string, exceptionId: string, payload: { quantity: number; note?: string }) {
  return api.post<{ id: string }>(`/api/v2/orders/${encodeURIComponent(orderId)}/exceptions/${encodeURIComponent(exceptionId)}/return-inbound`, payload);
}

export function updateOrderInvoice(orderId: string, payload: { invoiceStatus: 'NOT_REQUIRED' | 'PENDING' | 'ISSUED'; invoiceType?: 'VAT_GENERAL' | 'VAT_SPECIAL' | null; invoiceTitle?: string | null; invoiceTaxNo?: string | null; invoiceNo?: string | null; invoiceIssuedAt?: string | null; invoiceNote?: string | null }) {
  return api.patch(`/api/v2/orders/${encodeURIComponent(orderId)}/invoice`, payload);
}

export function reviewOrderChange(orderId: string, changeRequestId: string, action: 'APPROVE' | 'REJECT', reviewNote: string) {
  return api.patch<{ success: true }>(`/api/v2/orders/${encodeURIComponent(orderId)}/change-requests/${encodeURIComponent(changeRequestId)}`, { action, reviewNote });
}

export function requestOrderChange(orderId: string, payload: { expectedUpdatedAt: string; reason: string; changes: { deliveryDate?: string | null; note?: string | null; shippingMethod?: string | null; recipientName?: string | null; recipientPhone?: string | null; recipientAddress?: string | null; customerId?: string; warehouseId?: string; shippingFee?: number; discountAmount?: number; actualShippingFee?: number; depositAmount?: number; lineQuantities?: { lineId: string; quantity: number }[]; lineUnitPrices?: { lineId: string; unitPrice: number }[]; removeLineIds?: string[]; addProducts?: { productId: string; quantity: number; unitPrice: number }[]; addBundles?: { bundleId: string; quantity: number; unitPrice: number }[] } }) {
  return api.post<{ id: string; status: string }>(`/api/v2/orders/${encodeURIComponent(orderId)}/change-requests`, payload);
}

export function acknowledgeOrderChange(orderId: string, changeRequestId: string) {
  return api.patch<{ success: true }>(`/api/v2/orders/${encodeURIComponent(orderId)}/change-requests/${encodeURIComponent(changeRequestId)}`, { action: 'ACKNOWLEDGE' });
}

export type OrderConfirmKind = 'cancel' | 'close' | 'refund' | 'delete';
export function confirmOrderAction(orderId: string, kind: OrderConfirmKind) {
  const path = `/api/v2/orders/${encodeURIComponent(orderId)}`;
  if (kind === 'delete') return api.delete<{ id: string }>(path);
  if (kind === 'refund') return api.post(`${path}/status`, { status: 'REFUND_REQUESTED' });
  return api.post(`${path}/${kind}`, {});
}

export function approveOrderWithFollower(orderId: string, followerUserId: string) {
  return api.post(`/api/v2/orders/${encodeURIComponent(orderId)}/approve`, { followerUserId });
}

export function rejectOrder(orderId: string, reason: string) {
  return api.post(`/api/v2/orders/${encodeURIComponent(orderId)}/reject`, { reason });
}

export function chooseOrderAssembly(orderId: string, mode: 'REQUIRED' | 'NOT_REQUIRED', assemblyLineId?: string) {
  return api.post(`/api/v2/orders/${encodeURIComponent(orderId)}/status`, {
    status: mode === 'REQUIRED' ? 'ASSEMBLING' : 'ASSEMBLED', assemblyMode: mode,
    assemblyLineId: mode === 'REQUIRED' ? assemblyLineId : null,
  });
}

/** 复用 V1 组装完成事务，更新同一张订单的状态和完成时间。 */
export function completeOrderAssembly(orderId: string) {
  return api.post(`/api/v2/orders/${encodeURIComponent(orderId)}/status`, { status: 'ASSEMBLED' });
}

/**
 * 视图 = 桌面稿的视图栏 / 手机的横滑 chips。
 * 普通视图复用老接口；「我的待办」由独立的只读 V2 GET 聚合。
 */
export type ViewKey = 'tasks' | 'all' | 'review' | 'changes' | 'ack' | 'changeReview' | 'shortage' | 'ready' | 'assembling' | 'partial' | 'exceptions' | 'unassigned';

export const VIEWS: { key: ViewKey; label: string; query: Record<string, string>; count: (s: OrderStats) => number; perms?: string[] }[] = [
  { key: 'tasks', label: '我的待办', query: {}, count: () => -1 },
  { key: 'all', label: '全部订单', query: {}, count: (s) => s.all },
  { key: 'review', label: '等我审核', query: { status: 'SUBMITTED' }, count: (s) => statusCount(s, 'SUBMITTED'), perms: ['orders.approve'] },
  { key: 'changes', label: '有变更', query: { changeState: 'changed' }, count: (s) => s.orderChanges.changed },
  { key: 'ack', label: '待跟单知晓', query: { changeState: 'pendingAcknowledgement' }, count: (s) => s.orderChanges.pendingAcknowledgement, perms: ['orders.change.acknowledge'] },
  { key: 'changeReview', label: '变更待审', query: { changeState: 'pendingReview' }, count: (s) => s.orderChanges.pendingReview, perms: ['orders.change.approve'] },
  { key: 'ready', label: '可发货', query: { stockStatus: 'READY' }, count: () => -1 },
  { key: 'shortage', label: '缺货', query: { stockStatus: 'SHORTAGE' }, count: () => -1 },
  { key: 'assembling', label: '组装中', query: { status: 'ASSEMBLING' }, count: (s) => statusCount(s, 'ASSEMBLING') },
  { key: 'partial', label: '部分发货', query: { status: 'PARTIALLY_SHIPPED' }, count: (s) => statusCount(s, 'PARTIALLY_SHIPPED') },
  { key: 'exceptions', label: '异常未闭环', query: { exceptionStatus: 'open' }, count: (s) => s.exceptions?.open ?? -1, perms: ['exceptions.read'] },
  { key: 'unassigned', label: '未分配跟单', query: { assignment: 'unassigned' }, count: (s) => s.unassigned, perms: ['orders.assign', 'orders.approve'] },
];

function statusCount(s: OrderStats, st: string) {
  return s.statuses.find((x) => x.value === st)?.count ?? 0;
}

/** 「更多筛选」参数：普通条件走旧 GET；日期范围走独立的 V2 GET。 */
export type Filters = {
  status?: string;
  stockStatus?: string;
  shop?: string;
  invoiceStatus?: string;
  assignment?: string;
  exceptionStatus?: string;
  changeState?: string;
  dateField?: string;
  dateFrom?: string;
  dateTo?: string;
};
export const FILTER_KEYS: (keyof Filters)[] = ['status', 'stockStatus', 'shop', 'invoiceStatus', 'assignment', 'exceptionStatus', 'changeState', 'dateField', 'dateFrom', 'dateTo'];

type ListState = {
  items: OrderListItem[];
  loadedKey?: string;
  total: number;
  page: number;
  hasNext: boolean;
  stats?: OrderStats;
  loading: boolean;
  error?: ApiError | Error;
};

/** 列表：切换条件时重置并取第一页；「加载更多」追加下一页。旧请求会被 abort，避免竞态（老版缺陷 #2） */
export function useOrderList(query: Record<string, string | undefined>, pageSize: number, append: boolean, path = '/api/orders') {
  const [state, setState] = useState<ListState>({ items: [], total: 0, page: 1, hasNext: false, loading: true });
  const ctrl = useRef<AbortController | null>(null);
  const key = JSON.stringify(query);

  const fetchPage = useCallback(
    async (page: number, reset: boolean) => {
      ctrl.current?.abort();
      const c = new AbortController();
      ctrl.current = c;
      setState((s) => {
        const changedQuery = !!s.loadedKey && s.loadedKey !== `${path}:${key}`;
        return {
          ...s,
          items: changedQuery ? [] : s.items,
          total: changedQuery ? 0 : s.total,
          page: changedQuery ? 1 : s.page,
          hasNext: changedQuery ? false : s.hasNext,
          stats: changedQuery ? undefined : s.stats,
          loadedKey: changedQuery ? undefined : s.loadedKey,
          loading: true,
          error: undefined,
        };
      });
      try {
        const data = await api.get<OrderPage>(
          path,
          { ...JSON.parse(key), page, pageSize, includeStock: 1, includeStats: page === 1 ? 1 : undefined },
          c.signal,
        );
        setState((s) => ({
          items: reset || !append ? data.items : [...s.items, ...data.items],
          loadedKey: `${path}:${key}`,
          total: data.pagination.total,
          page: data.pagination.page,
          hasNext: data.pagination.hasNext,
          stats: data.stats ?? s.stats,
          loading: false,
        }));
      } catch (e) {
        if ((e as Error).name === 'AbortError') return;
        setState((s) => ({ ...s, loading: false, error: e as Error }));
      }
    },
    [key, pageSize, append, path],
  );

  useEffect(() => {
    fetchPage(1, true);
    return () => ctrl.current?.abort();
  }, [fetchPage]);
  useEffect(() => {
    const refresh = () => { void fetchPage(1, true); };
    window.addEventListener('ims:v2:refresh', refresh);
    return () => window.removeEventListener('ims:v2:refresh', refresh);
  }, [fetchPage]);

  return {
    ...state,
    current: state.loadedKey === `${path}:${key}`,
    reload: () => fetchPage(1, true),
    loadMore: () => state.hasNext && !state.loading && fetchPage(state.page + 1, false),
    goto: (p: number) => fetchPage(p, true),
  };
}

export function useResource<T>(path: string | null) {
  const [state, setState] = useState<{ data?: T; loading: boolean; error?: Error }>({ loading: !!path });
  const [nonce, setNonce] = useState(0);
  useEffect(() => {
    const refresh = () => setNonce((value) => value + 1);
    window.addEventListener('ims:v2:refresh', refresh);
    return () => window.removeEventListener('ims:v2:refresh', refresh);
  }, []);
  useEffect(() => {
    if (!path) return;
    const c = new AbortController();
    setState((s) => ({ ...s, loading: true, error: undefined }));
    api
      .get<T>(path, undefined, c.signal)
      .then((data) => setState({ data, loading: false }))
      .catch((e) => {
        if ((e as Error).name !== 'AbortError') setState((current) => ({ ...current, loading: false, error: e as Error }));
      });
    return () => c.abort();
  }, [path, nonce]);
  return { ...state, reload: () => setNonce((n) => n + 1) };
}

export function updateOrderDraft(id: string, payload: Record<string, unknown>) { return api.patch<{ id: string; orderNo: string }>(`/api/v2/orders/${encodeURIComponent(id)}`, payload); }
